import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Card from '../components/Structure/Card.tsx';
import Button from '../components/Form/Button.tsx';
import { ApiResponseType } from '../features/api/types.ts';
import { useAuthlessApi } from '../features/auth/hooks/use-authless-api.ts';
import { useAuthContext } from '../features/auth/hooks/use-auth-context.ts';
import AuthClientStore from '../features/auth/client-store/auth-client-store.ts';
import { toAccessToken } from '../interface/AccessToken.ts';

const MIN_LOADING_MS = 1500;

export default function RegistrationConfirm() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { activateAccount } = useAuthlessApi();
    const { checkLoginStatus } = useAuthContext();

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const redirectTimeout = useRef<any>(null);
    const minDelayTimeout = useRef<any>(null);

    useEffect(() => {
        const startedAt = Date.now();

        const applyResultWithMinDelay = (apply: () => void) => {
            const remaining = Math.max(0, MIN_LOADING_MS - (Date.now() - startedAt));
            minDelayTimeout.current = setTimeout(apply, remaining);
        };

        activateAccount(token).then(data => {
            let accessToken = toAccessToken(data.payload);
            if (!accessToken && data.payload && typeof data.payload === 'object') {
                const payloadObj:any = data.payload;
                accessToken = toAccessToken(payloadObj.payload) || toAccessToken(payloadObj.token);
            }

            if (data.type === ApiResponseType.OK && accessToken) {
                applyResultWithMinDelay(() => {
                    AuthClientStore.setAccessToken(accessToken);
                    setStatus('success');
                    redirectTimeout.current = setTimeout(() => {
                        checkLoginStatus();
                        navigate('/');
                    }, 2000);
                });
            } else {
                applyResultWithMinDelay(() => {
                    const payloadObj:any = data.payload;
                    setStatus('error');
                    setErrorMessage((payloadObj && payloadObj.error) ? payloadObj.error : 'Ihre Registrierung konnte nicht bestätigt werden. Der Link ist möglicherweise ungültig oder abgelaufen.');
                });
            }
        }).catch(() => {
            applyResultWithMinDelay(() => {
                setStatus('error');
                setErrorMessage('Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.');
            });
        });

        return () => {
            if (redirectTimeout.current) {
                clearTimeout(redirectTimeout.current);
            }
            if (minDelayTimeout.current) {
                clearTimeout(minDelayTimeout.current);
            }
        };
    }, []);

    if (status === 'loading') {
        return (
            <Card p={'var(--spacing-2xl)'} gap={'var(--spacing-xl)'}>
                <h1 className="h3 align-center">Registrierung wird bestätigt</h1>
                <p className="m-0 align-center">Ihre Registrierung wird bestätigt. Bitte haben Sie einen Moment Geduld.</p>
                <div className="loader inline"></div>
            </Card>
        );
    }

    if (status === 'success') {
        return (
            <Card p={'var(--spacing-2xl)'} gap={'var(--spacing-xl)'}>
                <div className="message success" role="alert">
                    <span>Registrierung bestätigt. Sie werden angemeldet</span>
                </div>
                <div className="loader inline"></div>
            </Card>
        );
    }

    return (
        <Card p={'var(--spacing-2xl)'} gap={'var(--spacing-xl)'}>
            <div className="message error" role="alert">
                <span>{errorMessage}</span>
            </div>
            <p className="m-0 text-xs align-center">
                <Link to={`/profile`}>
                    <Button
                        fullWidth
                        buttonType='primary'
                    >zurück zur Anmeldung</Button>
                </Link>
            </p>
        </Card>
    );
}
